import React from "react";
import { motion } from "framer-motion";
import {
  FaGithub,
  FaExternalLinkAlt,
  FaRocket,
  FaDatabase,
  FaCogs,
} from "react-icons/fa";

const Projects = () => {
  const projects = [
    {
      title: "Global Logistics & Courier Portal",
      tag: "MERN Stack & Logic",
      description:
        "A feature-rich customer portal developed for a logistics firm. It includes real-time invoice tracking, dynamic freight calculations based on regional zones, and a custom reward points system.",
      highlight: "Implemented complex calculation logic for 100+ active users.",
      tech: ["React", "Laravel", "MySQL", "Hostinger"],
      icon: FaRocket,
      image: process.env.PUBLIC_URL + "/projects/courier.png",
      liveUrl: "https://app.source1couriersgy.com/",
    },
    {
      title: "Business Portfolio & Services Website",
      tag: "Performance & Branding",
      description:
        "A professional, high-performance static website built to showcase company services. Focused on speed, clean UI, and consistent branding.",
      highlight: "Perfect performance score & 100% mobile responsiveness.",
      tech: ["Next.js", "React", "Tailwind", "Vercel"],
      icon: FaExternalLinkAlt,
      image: process.env.PUBLIC_URL + "/projects/s1s.png",
      liveUrl: "https://source1couriersgy.com/",
    },
    {
      title: "Football League Prediction App",
      tag: "Predict the Game. Own the Pitch.",
      description:
        "A high-performance web application designed for football enthusiasts. It leverages real-time data to provide match predictions, league standings, and detailed team statistics. Built with a focus on data accuracy and a seamless user experience.",
      highlight:
        "Live match updates via REST APIs, dynamic league tables, and predictive algorithms based on historical performance",
      tech: ["Laravel", "PHP", "MySQL", "REST API", "Excel Sync"],
      icon: FaDatabase,
      image: process.env.PUBLIC_URL + "/projects/football.jpeg",
      liveUrl: "https://football.sterlingbpo.com.au/",
    },
    {
      title: "Bamboo Villa Website",
      tag: "Hospitality & SEO",
      description:
        "Next.js 5-page highly optimized website with full SEO integration including Sitemap, Open Graph, and Hotel Schema for maximum visibility.",
      highlight: "Performance & SEO mastery with core web vitals optimization.",
      tech: ["Next.js", "React", "Tailwind", "SEO"],
      icon: FaExternalLinkAlt,
      image: process.env.PUBLIC_URL + "/projects/bambo.png",
      liveUrl: "https://www.bamboovillaahangama.com/",
    },
    {
      title: "Google Maps Popular Times Scraper",
      tag: "Automation",
      description:
        "Powerful automation tool built with Python and Selenium to extract live occupancy data into Excel/JSON for business analysis.",
      highlight: "Advanced Data Extraction",
      tech: ["Python", "Selenium", "Pandas", "Automation"],
      icon: FaCogs,
      image: process.env.PUBLIC_URL + "/projects/gg.png",
      liveUrl: "#",
    },
  ];

  return (
    <section id="projects" className="section-padding relative overflow-hidden">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Featured <br />
            <span className="text-gradient">Case Studies</span>
          </h2>
          <p className="text-white/50 max-w-xl text-lg">
            A selection of projects that demonstrate my ability to solve complex
            problems and deliver high-end digital experiences.
          </p>
        </motion.div>

        <div className="space-y-32">
          {projects.map((project, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className={`flex flex-col ${i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-12 lg:gap-20 items-center`}
            >
              {/* Image Side */}
              <div className="flex-1 w-full relative group">
                <div className="absolute inset-0 bg-primary/20 blur-[100px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10" />
                <div className="relative aspect-[16/9] rounded-[2rem] overflow-hidden glass border-white/5 group-hover:border-white/20 transition-all duration-700">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent opacity-40" />

                  {/* Floating Tech Badges */}
                  <div className="absolute bottom-6 left-6 flex flex-wrap gap-2">
                    {project.tech.map((t, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 text-[10px] uppercase font-bold tracking-widest glass-dark rounded-full text-white/70"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content Side */}
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-4">
                  <span className="text-accent-cyan font-display font-bold tracking-[0.2em] text-sm uppercase">
                    {project.tag}
                  </span>
                  <div className="h-[1px] w-12 bg-white/10" />
                </div>

                <h3 className="text-3xl md:text-5xl font-bold leading-tight">
                  {project.title}
                </h3>

                <p className="text-white/60 text-lg leading-relaxed">
                  {project.description}
                </p>

                <div className="p-6 glass-dark rounded-2xl border-l-2 border-primary">
                  <p className="text-sm font-bold text-white/40 uppercase tracking-widest mb-1">
                    Core Highlight
                  </p>
                  <p className="text-white font-medium">{project.highlight}</p>
                </div>

                <div className="flex gap-6 pt-4">
                  {project.liveUrl && (
                    <motion.a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-2 text-white font-bold group"
                    >
                      View Live Portal
                      <FaExternalLinkAlt className="text-primary group-hover:translate-x-1 transition-transform" />
                    </motion.a>
                  )}
                  {project.githubUrl && (
                    <motion.a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-2 text-white/50 font-bold group hover:text-white transition-colors"
                    >
                      GitHub Source
                      <FaGithub className="group-hover:translate-x-1 transition-transform" />
                    </motion.a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
